import { ReactNode, useState } from "react";
import { APP_VERSION } from "@/lib/appVersion";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

import { usePermissions } from "@/hooks/usePermissions";
import { useNotifications } from "@/hooks/useNotifications";
import { useProfile } from "@/hooks/useProfile";
import { ClipboardList, User, Shield, Activity, Bell, Wrench, Lightbulb, Menu, HelpCircle, KeyRound, Lock } from "lucide-react";

import type { NavItem } from "@/hooks/usePermissions";
import seacoastLogo from "@/assets/seacoast-logo.jpg";
import GuidedTour from "@/components/GuidedTour";

interface LayoutProps {
  children: ReactNode;
}

const Layout = ({ children }: LayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  
  const { can, isVisible, orderedNavItems, morePosition, landingPage } = usePermissions();
  const { unreadCount, notifications, markAsRead, markAllRead } = useNotifications();
  const { initials } = useProfile();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  const ENTITY_ICONS: Record<string, typeof Wrench> = {
    find: ClipboardList,
    deficiencies: ClipboardList,
    assets: Wrench,
    activity_log: Activity,
    users: Shield,
    future_enhancements: Lightbulb,
    auth_log: KeyRound,
    permissions: Lock,
  };

  // Split nav items based on morePosition
  const moreIdx = morePosition - 1; // morePosition is 1-indexed
  const beforeMore = orderedNavItems.slice(0, moreIdx);
  const afterMore = orderedNavItems.slice(moreIdx);
  const hasMore = afterMore.length > 0;

  // Build bottom tabs: items before More, then More tab, then items after (if More is not last)
  const tabs = [
    ...beforeMore.map((item, i) => ({
      path: item.path,
      icon: ENTITY_ICONS[item.entity] || Wrench,
      label: item.label,
      position: i,
    })),
    ...(hasMore ? [{ path: "/more", icon: Menu, label: "More", position: moreIdx }] : []),
  ];

  const moreItems = [
    ...afterMore.map((item) => ({
      path: item.path,
      icon: ENTITY_ICONS[item.entity] || Wrench,
      label: item.label,
    })),
  ];

  const handleSignOut = async () => {
    await signOut();
    navigate("/auth");
  };

  const isMoreActive = moreItems.some((item) => location.pathname === item.path);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Top bar */}
      <header className="bg-card border-b border-border px-4 py-3 flex items-center justify-between shrink-0">
        <button
          data-tour="logo"
          onClick={() => {
            localStorage.removeItem("last_viewed_asset");
            navigate(landingPage);
          }}
          className="flex items-center gap-2"
        >
          <img src={seacoastLogo} alt="Seacoast Fleet" className="h-8 w-auto" />
          <h1 className="text-industrial-lg font-black text-primary tracking-tight">
            Seacoast Fleet
          </h1>
          <span className="text-[9px] font-medium text-muted-foreground/60 ml-1 self-end mb-0.5">v{APP_VERSION}</span>
        </button>
        <div className="flex items-center gap-1">
          {/* Help */}
          <button
            data-tour="help"
            onClick={() => setShowTour(true)}
            className="p-2 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Feature Tour"
            title="Feature Tour"
          >
            <HelpCircle className="w-5 h-5" />
          </button>
          {/* Notifications bell */}
          <button
            data-tour="notifications"
            onClick={() => setShowNotifications(!showNotifications)}
            className="relative p-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-danger text-primary-foreground text-[10px] font-bold rounded-full flex items-center justify-center">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>
          <button
            data-tour="profile"
            onClick={() => navigate("/profile")}
            className="p-1.5 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Profile"
          >
            <div className="w-8 h-8 rounded-full bg-primary/10 border border-border flex items-center justify-center">
              {initials ? (
                <span className="text-xs font-bold text-primary leading-none">{initials}</span>
              ) : (
                <User className="w-4 h-4 text-primary" />
              )}
            </div>
          </button>
        </div>
      </header>

      {/* Notifications dropdown */}
      {showNotifications && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowNotifications(false)} />
          <div className="absolute right-2 top-14 z-50 w-80 max-h-96 overflow-auto bg-card border border-border rounded-lg shadow-lg">
            <div className="flex items-center justify-between p-3 border-b border-border">
              <p className="font-bold text-sm text-foreground">Notifications</p>
              {unreadCount > 0 && (
                <button onClick={markAllRead} className="text-xs text-primary font-semibold hover:underline">
                  Mark all read
                </button>
              )}
            </div>
            {notifications.length === 0 ? (
              <p className="p-4 text-sm text-muted-foreground text-center">No notifications</p>
            ) : (
              notifications.slice(0, 20).map((n) => (
                <button
                  key={n.id}
                  onClick={() => {
                    if (!n.read) markAsRead(n.id);
                    if (n.link) navigate(n.link);
                    setShowNotifications(false);
                  }}
                  className={`w-full text-left p-3 border-b border-border/50 hover:bg-secondary transition-colors ${!n.read ? "bg-primary/5" : ""}`}
                >
                  <p className="text-sm font-semibold text-foreground">{n.title}</p>
                  <p className="text-xs text-muted-foreground line-clamp-2">{n.message}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString()}</p>
                </button>
              ))
            )}
          </div>
        </>
      )}

      {/* Content */}
      <main className="flex-1 overflow-auto pb-20">
        {children}
      </main>

      {/* More menu overlay */}
      {showMoreMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowMoreMenu(false)} />
          <div className="fixed bottom-16 right-0 left-0 z-50 mx-4 mb-2 bg-card border border-border rounded-lg shadow-lg overflow-hidden">
            {moreItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setShowMoreMenu(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 transition-colors ${
                    isActive ? "text-primary bg-primary/5" : "text-foreground hover:bg-secondary"
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="text-sm font-semibold">{item.label}</span>
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Bottom tabs */}
      <nav className="fixed bottom-0 left-0 right-0 bg-primary flex z-50 px-2 py-2 gap-1">
        {tabs.map((tab, idx) => {
          const isActive = tab.path === "/more"
            ? isMoreActive
            : tab.path === "/assets"
            ? location.pathname === "/assets"
            : location.pathname === tab.path;
          const handleClick = () => {
            if (tab.path === "/more") {
              setShowMoreMenu(!showMoreMenu);
            } else {
              setShowMoreMenu(false);
              if (tab.path === "/assets" && location.pathname === "/assets") {
                return;
              }
              if ((tab as any).filter) {
                navigate(`${tab.path}?tab=${(tab as any).filter}`);
              } else {
                navigate(tab.path);
              }
            }
          };
          return (
            <button
              key={`${tab.path}-${idx}`}
              data-tour={`tab-${tab.label.toLowerCase()}`}
              onClick={handleClick}
              className={`flex-1 flex flex-col items-center gap-1 py-2 rounded-full transition-colors ${
                isActive
                  ? "bg-white/95 text-primary"
                  : "text-primary-foreground/70 hover:text-primary-foreground"
              }`}
            >
              <tab.icon className="w-5 h-5" />
              <span className="text-[10px] font-semibold">{tab.label}</span>
            </button>
          );
        })}
      </nav>

      {/* Guided Tour */}
      <GuidedTour active={showTour} onClose={() => setShowTour(false)} />
    </div>
  );
};

export default Layout;
